// 편집 캔버스 body의 A4 낱장 분할용 DOM 측정 루프(순수, React 비의존). admin EditorPage의
// measure()에서 추출 — web 상세 낱장 뷰도 같은 측정을 재사용할 수 있게 rc로 승격(P1).
//
// 측정은 전부 `data-composer-*` 속성 셀렉터(data-composer-node-id / data-composer-row /
// data-composer-repeat-item(s) / data-composer-fill-natural)로 이뤄져 에디터 상태에 비의존이다.
// 반환한 MeasuredNode[]를 planEditPages로 배치 계산한다(분리 = 단위 테스트 가능).
//
// stale plan 클립(데이터 증가로 새 행/항목이 잘림) 감지는 setState 의존이라 이 순수 함수 밖에서
// 처리한다 — measureBody는 "클립 감지" 신호({ clipped: true })만 반환하고, plan 리셋은 호출측 책임.

import type { ComposerNode, RepeatNode } from '@safetyhub/shared';
import { computeSafeCutRows } from './pagination';
// 분할 경계(유일한 안쪽 repeat) 판정은 렌더(RenderTemplate)와 **같은 함수**를 쓴다 — 측정이 낸
// 높이와 렌더가 그리는 형상이 갈리면 계획이 틀린 높이로 자른다.
import {
  chunkFlowActive,
  findSoleNestedBoundary,
  findSoleUnitFillTable,
  unitFlowChunks,
  unitHasBreakDecl,
} from './nestedRepeatBoundary';
// 최상위 branch는 계획 층에서 **투명하게 통과**시킨다(렌더가 이미 투명 컨테이너라 출력이 같다) —
// 그래야 branch 안의 table/repeat이 분할 경계를 되찾는다. 판정·평탄화는 branchPagination 단일 소스.
import { flattenBodyForMeasure } from './branchPagination';
import type { BranchPaginationContext } from './branchPagination';
import { innerAxisTotalH, innerAxisUnitCount } from './pagePlan';
import type {
  MeasuredInnerAxis,
  MeasuredNode,
  MeasuredRepeat,
  MeasuredRepeatInner,
  MeasuredUnitChunks,
} from './pagePlan';

/** measureBody 반환: 정상 측정치(MeasuredNode[]) 또는 stale plan 클립 신호(호출측이 plan 리셋 후 재측정). */
export type MeasureResult = MeasuredNode[] | { clipped: true };

/**
 * measureBody 선택 입력. **전부 생략하면 종전과 완전히 같게 동작한다**(기존 호출·spec 회귀 0).
 *  - data/editing/activeBranchSlots: 최상위 branch 투명 통과(활성 슬롯 판정)에 필요한 것.
 *    미지정이면 빈 데이터로 조건을 평가하므로, 조건 슬롯이 있는 branch는 사실상 하강하지 않는다.
 *  - heightOf: 높이 측정기 주입(기본 offsetHeight). jsdom은 레이아웃이 없어 offsetHeight가 0이라
 *    spec이 높이를 준다 — collectRepeatInstances/measureNestedBoundary와 같은 규약.
 */
export interface MeasureBodyOptions extends BranchPaginationContext {
  heightOf?: (el: HTMLElement) => number;
}

// ── 중첩 반복 인스턴스 좌표 (P6-0 스파이크) ──────────────────────────────────
// 중첩 반복에서 안쪽 repeat은 노드 1개인데 인스턴스가 바깥 항목 수만큼 렌더된다. 마커
// `data-composer-repeat-item="{repeatId}::{i}"`만 보면 인스턴스가 구분되지 않아 높이가 서로
// 덮어써진다. 해결은 **조상 체인**이다 — 항목 wrapper의 부모 쪽에 바깥 항목 wrapper가 그대로
// 있으므로 마커 포맷을 바꾸지 않고 경로를 복원할 수 있다(기존 마커 소비처 회귀 0).

/** 인스턴스 경로 1구간 파싱 — `{nodeId}::{index}`. 마지막 `::` 기준(id에 `::` 없음이 불변식). */
function parseSegment(marker: string): { nodeId: string; index: number } | null {
  const at = marker.lastIndexOf('::');
  if (at < 0) return null;
  const index = Number(marker.slice(at + 2));
  if (!Number.isFinite(index)) return null;
  return { nodeId: marker.slice(0, at), index };
}

/**
 * 반복 항목 wrapper 요소의 **인스턴스 경로**를 조상 체인에서 복원한다.
 * 예) 사진 3번 안의 발견사항 1번 wrapper → `rp-photos::3/rp-findings::1`.
 * 최상위 반복 항목은 자기 마커 그대로(`rp-photos::3`) — 렌더의 ctx.instancePath와 같은 문자열이다.
 * 마커가 없거나 형식이 아니면 null.
 */
export function repeatInstancePath(itemEl: HTMLElement): string | null {
  const own = itemEl.getAttribute('data-composer-repeat-item');
  if (own == null || parseSegment(own) == null) return null;
  const segs = [own];
  let cur: HTMLElement | null = itemEl.parentElement;
  while (cur) {
    const anc: HTMLElement | null = cur.closest<HTMLElement>('[data-composer-repeat-item]');
    if (!anc) break;
    const m = anc.getAttribute('data-composer-repeat-item');
    if (m != null && parseSegment(m) != null) segs.push(m);
    cur = anc.parentElement;
  }
  return segs.reverse().join('/');
}

/** 한 repeat **인스턴스**의 항목 높이 수집 결과(rangeKey = 렌더/계획이 쓰는 조회 키). */
export interface RepeatInstanceHeights {
  /** 조회 키 — `{바깥경로}/{repeatId}`(최상위면 `{repeatId}`). itemRanges 키와 동일 어휘. */
  rangeKey: string;
  /** 항목 인덱스별 높이(px). 미렌더 인덱스는 hole. */
  heights: number[];
  /** 실제로 측정된 항목 수(클립 감지용 — declaredTotal과 비교). */
  rendered: number;
  /**
   * 이 인스턴스 컨테이너가 선언한 클립 무관 총 항목수(data-composer-repeat-items). 미발견이면 undefined.
   * 이 필드가 있는 이유(과거형 — 지금은 해소됨): P6-0 이전에는 이 값을 `querySelector`(첫 매칭 1개)로
   * 읽어, 중첩에서 같은 nodeId 컨테이너가 인스턴스 수만큼 있을 때 **첫 인스턴스의 총수**만 읽혔다
   * (사진1=3건·사진2=2건인데 둘 다 3으로 판정 → 클립 오탐). 지금은 `measureRepeatInstance`가
   * 인스턴스별로 이 필드를 읽어 그 오탐이 없다.
   */
  declaredTotal?: number;
  /**
   * 같은 (rangeKey, index)가 2번 이상 렌더됨 = **측정 원자성 위반**. 한 항목이 여러 낱장에 조각으로
   * 나뉘면 각 조각의 offsetHeight는 전체 높이가 아니라 계획이 만든 값이라, 그대로 재측정하면
   * 측정↔계획 피드백 루프(진동)가 된다. 계획기는 이 신호가 뜬 축을 측정 단위로 쓰면 안 된다.
   */
  duplicated: boolean;
}

/**
 * container 안에서 repeat 노드 `nodeId`의 항목 높이를 **인스턴스별로 분리 수집**한다(순수).
 * 비중첩이면 결과가 1개이고 rangeKey === nodeId라 기존 measureRepeat의 heights와 동일하다.
 *
 * heightOf는 주입 가능(기본 offsetHeight) — jsdom은 레이아웃이 없어 0이므로 spec이 높이를 주입한다.
 */
export function collectRepeatInstances(
  container: HTMLElement,
  nodeId: string,
  heightOf: (el: HTMLElement) => number = (el) => el.offsetHeight,
): RepeatInstanceHeights[] {
  const byKey = new Map<string, RepeatInstanceHeights>();
  const entryFor = (rangeKey: string): RepeatInstanceHeights => {
    let e = byKey.get(rangeKey);
    if (!e) {
      e = { rangeKey, heights: [], rendered: 0, duplicated: false };
      byKey.set(rangeKey, e);
    }
    return e;
  };
  // 인스턴스별 총 항목수 선언(컨테이너) 먼저 — 중첩에서는 같은 nodeId 컨테이너가 인스턴스 수만큼이라
  // querySelector 단일 조회로는 첫 인스턴스 값만 읽힌다(위 declaredTotal 주석).
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  for (const contEl of container.querySelectorAll<HTMLElement>(
    `[data-composer-node-id="${esc(nodeId)}"][data-composer-repeat-items]`,
  )) {
    const outer = contEl.parentElement?.closest<HTMLElement>('[data-composer-repeat-item]') ?? null;
    const outerPath = outer ? repeatInstancePath(outer) : null;
    const total = Number(contEl.getAttribute('data-composer-repeat-items'));
    if (!Number.isFinite(total)) continue;
    entryFor(outerPath ? `${outerPath}/${nodeId}` : nodeId).declaredTotal = total;
  }
  for (const itemEl of container.querySelectorAll<HTMLElement>('[data-composer-repeat-item]')) {
    const own = parseSegment(itemEl.getAttribute('data-composer-repeat-item') ?? '');
    if (!own || own.nodeId !== nodeId) continue;
    const path = repeatInstancePath(itemEl);
    if (path == null) continue;
    // rangeKey = 경로에서 자기 항목 인덱스를 뗀 것(= 바깥경로 + repeat 노드).
    const entry = entryFor(path.slice(0, path.lastIndexOf('::')));
    if (entry.heights[own.index] !== undefined) entry.duplicated = true;
    // 채움 행(반복 단위 안 pageFill 표 확장분) 차감 = 자연 높이 — 확장분이 되먹으면 측정↔계획
    // 진동이 된다(ownedFillRowsHeight 주석). 채움 행이 없으면 0이라 종전과 동일(회귀 0).
    entry.heights[own.index] = Math.max(
      0,
      heightOf(itemEl) - ownedFillRowsHeight(itemEl, itemEl, heightOf),
    );
    entry.rendered += 1;
  }
  return [...byKey.values()];
}

// ── 중첩 반복 분할의 **측정 불변식** (P6-1) ────────────────────────────────
// 한 반복 항목이 안쪽 반복 항목 경계에서 쪼개지면, 그 항목 wrapper는 낱장 수만큼 중복 방출되고
// 각 조각의 offsetHeight는 **항목 전체가 아니라 계획이 만든 값**이다(P6-0 스파이크 C에서 고정).
// 그래서 계획 입력은 조각 높이가 아니라 아래 **조각 수에 불변인 3종**이어야 한다:
//
//   ① 안쪽 항목 높이 u_k  — 안쪽 항목은 조각나지 않고 온전히 렌더되므로 정확히 한 번 측정된다
//                           (collectRepeatInstances의 duplicated=false가 그 증명이다).
//   ② lead 높이 L        — 고정분(사진·설명 등). **첫 조각에만** 렌더된다.
//   ③ cont 높이 C        — 체인 컨테이너의 padding/border 등 continuation 조각에도 남는 부분.
//
// 조각 높이 = (첫 조각이면 L, 아니면 C) + 그 조각의 안쪽 단위 합 + 안쪽 gap.
//
// ⚠️ L을 "(항목 높이 − 안쪽 컨테이너 높이)의 조각별 최댓값"으로 추정하면 안 된다 — 항목에
// minHeight/stretch가 걸리면 continuation 조각의 값이 더 커질 수 있어 최댓값이 lead가 아닌 것을
// 집는다. 그래서 렌더가 **lead를 실제로 그린 조각에 `data-composer-lead` 마커를 방출**하고,
// 측정은 그 조각 하나에서 같은-조각 뺄셈으로 L을 얻는다(휴리스틱 없음).
//
// C는 통짜 상태에서는 **관측 자체가 불가능**하다(continuation 조각이 없다). 그래서 undefined를
// 그대로 돌려주고, 계획기가 "분할 여부 결정에는 C를 쓰지 않는다"는 규약으로 흡수한다 — C가 나중에
// 관측돼도 분할 결정이 뒤집히지 않아야 통짜↔분할 진동이 생기지 않는다.

/** 중첩 경계 측정이 신뢰 불가한 사유 — 하나라도 있으면 계획기는 그 경계를 쓰면 안 된다(종전 동작 degrade). */
export type NestedBoundaryViolation =
  /** lead 마커를 단 조각이 없다 — 렌더 형상 규약 이탈(측정이 L을 정할 근거가 없다). */
  | 'no-lead-fragment'
  /** lead 마커를 단 조각이 둘 이상 — 같은 고정분이 여러 낱장에 그려졌다(형상 규약 이탈). */
  | 'lead-fragment-multiple'
  /** 항목 안에서 안쪽 반복 컨테이너를 못 찾았다(0건 렌더 등). */
  | 'inner-container-missing'
  /**
   * continuation 조각들의 C가 서로 다르다 = 조각 경계에 의존 → 계획식이 성립하지 않는다.
   * ⚠️ 이 위반만은 **경계를 버리지 않는다**(버리면 통짜↔분할 진동) — 보수적으로 max(C)를 쓰고
   * 사실만 신호로 남긴다. 나머지 위반은 렌더 형상 규약 이탈(=버그 신호)이라 종전 동작으로 degrade.
   */
  | 'cont-fragments-differ'
  /**
   * 경계 컨테이너 높이 > 그 안 단위 높이 합 = **재구성식의 전제가 깨졌다**.
   *
   * 재구성은 `항목 높이 = L + Σ단위`인데, L은 같은 조각의 (wrapper − 컨테이너)라 컨테이너에만 있고
   * 단위 합에는 없는 여분 δ가 **양쪽 어디에도 안 들어간다** → 항목이 δ만큼 과소 계상되고, 계획은
   * "들어간다"고 오판해 통짜로 배치한 낱장이 넘친다. δ가 생기는 저작:
   *  - 교차축 기본 채움(`layout.ts` — `direction:'row'` 그룹 안 컨테이너는 `alignSelf:'stretch'`),
   *    주축 `height:'fill'`(flexGrow:1/flexBasis:0), 또는 컨테이너 `height`/`minHeight` 지정
   *  - 컨테이너 자체의 세로 padding/border(단위 합에 없는 chrome)
   *  - 단위로 세지 않는 행(예: 표의 `data-composer-fill-row` 채움 행)
   *
   * δ는 조각 경계에 따라 달라져(형제 높이·잔여 공간이 낱장마다 다르다) 계획 시점에 알 수 없다 →
   * 보정이 아니라 **경계 폐기**가 옳다. 판정은 통짜 상태에서만 하므로(아래) degrade의 결과가 다시
   * 통짜라 진동이 성립하지 않는다.
   */
  | 'inner-container-oversized';

/**
 * 안쪽 경계 컨테이너를 식별하는 마커 속성 — repeat이면 항목 총수, 표면 행 총수를 선언하는 속성이
 * 그 컨테이너 element에 붙는다(각각 `RepeatNode` 컨테이너 div / `<table>`).
 */
export type InnerContainerAttr = 'data-composer-repeat-items' | 'data-composer-table-rows';

/**
 * `el`이 반복 항목 조각 `w`에 **직접** 속하는가 — 사이에 더 안쪽 반복 항목이 없으면 참.
 * 중첩에서 동명 노드가 여러 인스턴스로 렌더되므로, 조회 결과를 반드시 이 술어로 걸러야 다른 층의
 * 값이 섞이지 않는다.
 */
function ownedByFragment(el: HTMLElement, w: HTMLElement): boolean {
  return (el.parentElement?.closest<HTMLElement>('[data-composer-repeat-item]') ?? null) === w;
}

/**
 * 조각 `w`가 직접 소유한 **채움 행**(`data-composer-fill-row` — 렌더가 낱장 잔여를 채우려 페인팅한
 * 순수 시각 행) 높이 합(px). 반복 단위 안 채움 표(결정 2026-09-03)가 확장돼 있어도 측정이
 * **자연 높이**를 읽도록 wrapper/컨테이너 실측에서 이 값을 차감한다 — 확장 높이를 측정 입력으로
 * 되먹이지 않는다는 채움 제1원칙(결정 2026-07-02)의 반복 단위판. 최상위 채움 표는 행합 측정이라
 * 원래 자연 높이만 읽는다(여기 안 옴). 더 깊은 중첩 단위의 채움 행은 그 단위의 몫(소유 판정).
 */
function ownedFillRowsHeight(
  scope: HTMLElement,
  w: HTMLElement,
  heightOf: (el: HTMLElement) => number,
): number {
  let sum = 0;
  for (const fr of scope.querySelectorAll<HTMLElement>('[data-composer-fill-row]')) {
    if (ownedByFragment(fr, w)) sum += heightOf(fr);
  }
  return sum;
}

// ── 순차 다경계(결정 2026-09-03 심야) — 단위 흐름 청크 측정 ─────────────────
// 유일 경계가 없는 단위는 `unitFlowChunks` 청크열로 측정한다. 청크별 실측은 전부 인스턴스
// 스코프(ownedByFragment)라 중첩·동명 인스턴스가 섞이지 않고, 계획 입력은 조각 수에 불변인
// 값들이다: 구간 청크 = 요소 자연높이(소유 채움 행 차감), 경계 청크 = 기존 인스턴스 축 측정,
// chrome = 조각별 (wrapper − Σ소유 청크 요소) 최댓값(청크 밖 잔여 — branch 래퍼·template padding).

/** `scope` 요소의 자연높이 — 그 아래 조각 `w` 소유 채움 행을 뺀 값(확장분 미되먹임). */
function naturalHeightOf(
  scope: HTMLElement,
  w: HTMLElement,
  heightOf: (el: HTMLElement) => number,
): number {
  return Math.max(0, heightOf(scope) - ownedFillRowsHeight(scope, w, heightOf));
}

/**
 * 반복 항목 인스턴스 `itemPath`가 소유한 노드 `nodeId` 요소의 자연높이(px). 조각들 중 그 요소를
 * 품은 조각에서 읽는다(구간 청크는 조각나지 않아 많아야 한 조각에 있다). 미발견(비활성 branch
 * 슬롯 등 미렌더) = 0.
 */
export function measureOwnedNodeHeight(
  container: HTMLElement,
  itemPath: string,
  nodeId: string,
  heightOf: (el: HTMLElement) => number,
): number {
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  const marker = itemPath.slice(itemPath.lastIndexOf('/') + 1);
  for (const w of container.querySelectorAll<HTMLElement>(
    `[data-composer-repeat-item="${esc(marker)}"]`,
  )) {
    if (repeatInstancePath(w) !== itemPath) continue;
    for (const nEl of w.querySelectorAll<HTMLElement>(`[data-composer-node-id="${esc(nodeId)}"]`)) {
      if (ownedByFragment(nEl, w)) return naturalHeightOf(nEl, w, heightOf);
    }
  }
  return 0;
}

/**
 * 단위 chrome — 조각별 `(wrapper 자연높이 − Σ그 조각이 소유한 청크 요소 자연높이)`의 최댓값
 * (0 하한). 통짜 상태에서는 조각이 하나라 그 값이 곧 청크 밖 잔여(branch 래퍼·template padding)
 * 다. 분할 상태의 경계 청크 요소는 클립된 실측 그대로 쓴다(잔여 추출에는 그걸로 충분하고, C는
 * 분할 여부 판단에 안 쓰여 진동이 없다 — contHeight 규약과 동형). 프룬돼 사라진 래퍼의 chrome은
 * 과충전이 되지만 방향이 under-fill이라 넘치지 않는다.
 */
export function measureUnitChunkChrome(
  container: HTMLElement,
  itemPath: string,
  chunkIds: readonly string[],
  heightOf: (el: HTMLElement) => number,
): number {
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  const marker = itemPath.slice(itemPath.lastIndexOf('/') + 1);
  let best = 0;
  for (const w of container.querySelectorAll<HTMLElement>(
    `[data-composer-repeat-item="${esc(marker)}"]`,
  )) {
    if (repeatInstancePath(w) !== itemPath) continue;
    let sum = 0;
    for (const id of chunkIds) {
      for (const nEl of w.querySelectorAll<HTMLElement>(`[data-composer-node-id="${esc(id)}"]`)) {
        if (ownedByFragment(nEl, w)) {
          sum += naturalHeightOf(nEl, w, heightOf);
          break;
        }
      }
    }
    best = Math.max(best, naturalHeightOf(w, w, heightOf) - sum);
  }
  return Math.max(0, best);
}

/** 한 반복 항목의 중첩 분할 경계 측정치 — 조각 수·조각 경계에 **불변**이어야 한다. */
export interface NestedBoundaryMeasure {
  /** 항목 인스턴스 경로(`{repeatId}::{항목인덱스}`, 중첩이면 바깥→안쪽 `/` 연결). */
  path: string;
  /** 안쪽 repeat 노드 id(= 분할 경계). */
  innerNodeId: string;
  /** ② lead 높이 — 고정분 + 그 앞뒤 gap. lead 마커 조각에서 (항목 − 안쪽 컨테이너). */
  leadHeight: number;
  /**
   * ③ cont 높이 — continuation 조각에도 남는 체인 컨테이너 chrome.
   * **아직 조각이 없으면 undefined(미관측)** — 0으로 단정하지 않는다.
   */
  contHeight?: number;
  /**
   * lead 조각이 품은 **경계 컨테이너 자체**의 높이(px). 통짜 상태에서는 그 안 단위 높이 합과
   * 같아야 하고(= 재구성식의 전제), 어긋나면 `inner-container-oversized`가 선다.
   * lead 조각을 못 찾았거나 컨테이너가 없으면 undefined.
   */
  innerContentHeight?: number;
  /** 위반 사유(있으면 이 경계를 계획에 쓰지 않는다). */
  violation?: NestedBoundaryViolation;
}

/**
 * 재구성식 전제(`경계 컨테이너 높이 == 그 안 단위 높이 합`)를 **대조할 기준**. 호출측(축 측정을 이미
 * 가진 쪽)만 알 수 있으므로 주입받는다.
 *
 * ⚠️ **대조가 유효한 상태에서만 넘겨라**(호출측 책임). 클립된 조각의 컨테이너에는 단위가 일부만
 * 들어 있어 합이 작게 나오고, 그러면 정상 경계가 "부풀었다"로 오판돼 조용히 죽는다. 조각이
 * 여러 개인 상태(분할 중)는 이 함수가 자체적으로 걸러낸다 — 조각마다 단위가 나뉘어 있기 때문이다.
 */
export interface NestedBoundaryExpectation {
  /** 경계 축의 통짜 높이 — `innerAxisTotalH(axis)`. */
  innerTotalH: number;
  /** 그 높이를 만든 측정치 개수 — `innerAxisUnitCount(axis)`. 반올림 허용치 산정용. */
  unitCount: number;
}

/**
 * 반복 항목 `itemPath` 하나의 중첩 분할 경계(L·C)를 측정한다(순수 — heightOf 주입 가능).
 *
 * 한 항목이 여러 낱장에 조각나 있으면 wrapper가 그 수만큼 있다. 어느 조각이 고정분을 그렸는지는
 * **추정하지 않고** 렌더가 방출한 `data-composer-lead` 마커로 읽는다. 뺄셈은 항상 **같은 조각
 * 안에서**(wrapper − 그 조각의 안쪽 컨테이너) 하므로 조각 경계가 어디든 값이 같다.
 *
 * heightOf는 주입 가능(기본 offsetHeight) — jsdom은 레이아웃이 없어 0이므로 spec이 높이를 준다.
 *
 * `expect`를 주면 재구성식의 전제(컨테이너 높이 == 단위 합)까지 검증해 깨졌으면
 * `inner-container-oversized` 위반을 세운다 — 그 판정 규약은 아래 본문 주석 참조.
 */
export function measureNestedBoundary(
  container: HTMLElement,
  itemPath: string,
  innerNodeId: string,
  heightOf: (el: HTMLElement) => number = (el) => el.offsetHeight,
  innerAttr: InnerContainerAttr = 'data-composer-repeat-items',
  expect?: NestedBoundaryExpectation,
): NestedBoundaryMeasure {
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  const marker = itemPath.slice(itemPath.lastIndexOf('/') + 1);
  const out: NestedBoundaryMeasure = { path: itemPath, innerNodeId, leadHeight: 0 };
  const contChromes: number[] = [];
  let leadSeen = 0;
  let contMissing = false;
  for (const w of container.querySelectorAll<HTMLElement>(
    `[data-composer-repeat-item="${esc(marker)}"]`,
  )) {
    if (repeatInstancePath(w) !== itemPath) continue;
    // 이 조각이 **직접** 품은 안쪽 컨테이너만 — 더 깊은 중첩 인스턴스의 동명 컨테이너를 집으면
    // 뺄셈이 다른 층의 값을 섞는다(경로 소유권 확인이 그 방어다).
    let cont: HTMLElement | null = null;
    for (const c of w.querySelectorAll<HTMLElement>(
      `[data-composer-node-id="${esc(innerNodeId)}"][${innerAttr}]`,
    )) {
      if (ownedByFragment(c, w)) {
        cont = c;
        break;
      }
    }
    if (!cont) {
      contMissing = true;
      continue;
    }
    // 채움 행 차감 = 자연 높이(ownedFillRowsHeight 주석). 채움 표가 lead 소속이면 w 쪽에서만,
    // 경계 겸임(cont 자신이 채움 표)이면 양쪽에서 같은 값이 빠져 chrome(lead)·innerContentHeight
    // 모두 자연값이 된다 — inner-container-oversized 대조가 채움 확장분으로 오탐하지 않는다.
    const contH = heightOf(cont) - ownedFillRowsHeight(cont, w, heightOf);
    const chrome = heightOf(w) - ownedFillRowsHeight(w, w, heightOf) - contH;
    if (w.getAttribute('data-composer-lead') === itemPath) {
      leadSeen += 1;
      out.leadHeight = chrome;
      out.innerContentHeight = contH;
    } else {
      contChromes.push(chrome);
    }
  }
  if (contMissing) out.violation = 'inner-container-missing';
  else if (leadSeen === 0) out.violation = 'no-lead-fragment';
  else if (leadSeen > 1) out.violation = 'lead-fragment-multiple';
  else if (contChromes.length > 0) {
    // C는 조각 경계에 무관해야 한다 — 다르면 계획식(조각 높이 = C + 단위합)이 성립하지 않는다.
    // ⚠️ 이때 **경계를 버리지 않는다**. 버리면 통짜로 되돌아가고, 통짜에는 continuation이 없어
    // 위반이 사라져 다시 분할 → 통짜↔분할 주기 진동이 된다(degrade가 곧 진동의 원인이 되는 형태).
    // 대신 **최댓값**을 쓴다 — 보수적(조각당 항목이 줄 뿐 넘치지 않음)이고 분할을 유지하므로
    // 진동이 생기지 않는다. 위반 사실은 신호로 남겨 P6-2가 저작자에게 알린다.
    out.contHeight = Math.max(...contChromes);
    if (contChromes.some((c) => c !== contChromes[0])) out.violation = 'cont-fragments-differ';
  }
  // ── 재구성식 전제 검증 (`inner-container-oversized`) ────────────────────────
  // 조건이 셋인 이유는 전부 **오탐이 곧 기능 침묵사**이기 때문이다(정상 경계를 끄면 아무 신호 없이
  // 종전 통짜로 돌아간다). 그래서 "전제가 깨졌다"를 **증명할 수 있을 때만** 끈다:
  //
  //  ① 다른 위반이 없다 — 형상이 이미 이상하면 그쪽 처방이 먼저다(판정을 겹쳐 쌓지 않는다).
  //  ② 조각이 하나뿐이다(`contChromes.length === 0` — lead 1개 + continuation 0개). 쪼개진 상태에서는
  //     각 조각의 컨테이너에 **단위가 일부만** 들어 있어 `단위 합`과 견줄 대상이 애초에 아니다.
  //     이 상태로 견주면 δ가 음수로 크게 나와 무해하지만, 반대로 조각별 stretch가 δ를 양수로
  //     만들면 정상 경계를 죽인다. → 통짜 상태에서만 본다.
  //  ③ 호출측이 `expect`를 줬다 = 그 축이 **클립되지 않았다**(전 단위가 렌더됐다). 클립 상태의 합은
  //     당연히 작으므로 대조가 성립하지 않는다.
  //
  // 통짜 상태에서만 판정하므로 진동이 없다: degrade의 결과가 다시 통짜라 같은 판정이 반복될 뿐이고,
  // 초기 plan이 항상 통짜(PagedComposerView)라 부푼 경계는 **분할되기 전에** 걸러진다.
  //
  // 허용치는 반올림 상한이다 — offsetHeight는 정수 반올림이라 항 n개의 합과 컨테이너 실측이
  // 최대 0.5*(n+1)만큼 어긋날 수 있다(참 높이가 같아도). 여기에 계획기 EPS(1)를 얹어, 그 잡음으로는
  // 절대 넘을 수 없는 선을 넘었을 때만 위반으로 본다.
  if (out.violation === undefined && expect && contChromes.length === 0 && leadSeen === 1) {
    const contentH = out.innerContentHeight ?? 0;
    const tolerance = 1 + (expect.unitCount + 1) / 2;
    if (contentH - expect.innerTotalH > tolerance) out.violation = 'inner-container-oversized';
  }
  return out;
}

/**
 * 반복 항목 인스턴스 `itemPath` 안의 표 `nodeId`를 측정한다 — 최상위 `measureTable`과 **같은 산출**
 * (총 행수·행 top·안전 경계·클립 신호)이되 조회 범위가 그 항목의 조각들로 한정된다.
 *
 * 왜 인스턴스별인가: `data-composer-row`는 `{표id}::{행}`이라 인스턴스를 구분하지 않는다. 중첩에서
 * 같은 표 노드가 바깥 항목 수만큼 렌더되므로, 전역 조회로 재면 사진 1번의 행 높이가 사진 2번 것에
 * 덮어써진다(repeat의 `collectRepeatInstances`가 푸는 문제와 같다 — 해법도 같은 조상 체인이다).
 *
 * 한 항목이 여러 낱장에 조각나 있으면 각 조각이 자기 `rowRange`만 렌더한다. 범위는 서로 겹치지
 * 않으므로 조각들의 행을 합집합으로 모으면 원본 행 높이가 그대로 복원된다 — 반복 항목 wrapper와
 * 달리 **행 자체는 쪼개지지 않아** 조각 경계에 불변이다(P6-1 불변식 ①과 같은 근거).
 *
 * heightOf는 주입 가능(기본 offsetHeight) — jsdom은 레이아웃이 없어 0이므로 spec이 높이를 준다.
 */
export function measureTableInstance(
  container: HTMLElement,
  itemPath: string,
  nodeId: string,
  heightOf: (el: HTMLElement) => number = (el) => el.offsetHeight,
): {
  totalRows: number;
  tableHeight: number;
  rowTop: (r: number) => number;
  safe: boolean[];
  clipped: boolean;
} | null {
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  const marker = itemPath.slice(itemPath.lastIndexOf('/') + 1);
  const heights: number[] = [];
  const spanCells: { row: number; rowspan: number }[] = [];
  let rendered = 0;
  let declaredTotal = Number.NaN;
  const rowIndexOf = (el: HTMLElement | null): number => {
    const parts = (el?.getAttribute('data-composer-row') ?? '').split('::');
    return Number(parts[parts.length - 1]);
  };
  for (const w of container.querySelectorAll<HTMLElement>(
    `[data-composer-repeat-item="${esc(marker)}"]`,
  )) {
    if (repeatInstancePath(w) !== itemPath) continue;
    for (const tableEl of w.querySelectorAll<HTMLElement>(
      `[data-composer-node-id="${esc(nodeId)}"][data-composer-table-rows]`,
    )) {
      if (!ownedByFragment(tableEl, w)) continue;
      const t = Number(tableEl.getAttribute('data-composer-table-rows'));
      if (Number.isFinite(t)) declaredTotal = t;
    }
    for (const tr of w.querySelectorAll<HTMLElement>(`[data-composer-row^="${esc(nodeId)}::"]`)) {
      if (!ownedByFragment(tr, w)) continue;
      const r = rowIndexOf(tr);
      // 조각 범위는 서로 겹치지 않지만, 겹쳐 들어와도 행 수를 부풀리지 않게 distinct로 센다.
      if (!Number.isFinite(r) || heights[r] !== undefined) continue;
      heights[r] = heightOf(tr);
      rendered += 1;
    }
    for (const cellEl of w.querySelectorAll<HTMLElement>(
      `[data-composer-cell^="${esc(nodeId)}::"]`,
    )) {
      if (!ownedByFragment(cellEl, w)) continue;
      // 최상위 measureTable과 같은 규약 — keep-together(묶음) 그룹 셀만 안전 경계를 만든다.
      const fullAttr = cellEl.getAttribute('data-composer-rowspan-full');
      if (fullAttr == null) continue;
      const rs = Number(fullAttr);
      if (!(rs > 1)) continue;
      const r = rowIndexOf(cellEl.closest<HTMLElement>('[data-composer-row]'));
      if (Number.isFinite(r)) spanCells.push({ row: r, rowspan: rs });
    }
  }
  const totalRows = Number.isFinite(declaredTotal) ? declaredTotal : heights.length;
  if (totalRows === 0) return null;
  const rowTops: number[] = [];
  let acc = 0;
  for (let r = 0; r < totalRows; r += 1) {
    rowTops[r] = acc;
    acc += heights[r] ?? 0;
  }
  const tableHeight = acc;
  return {
    totalRows,
    tableHeight,
    rowTop: (r: number) => (r < totalRows ? rowTops[r] : tableHeight),
    safe: computeSafeCutRows(spanCells, totalRows),
    // 클립 감지: 렌더된 행 수 < 진짜 총 행수 → 현재 plan이 stale(데이터 증가로 새 행이 잘림).
    clipped: rendered < totalRows,
  };
}

/**
 * 렌더된 편집 캔버스 컨테이너(container)에서 body 노드들을 측정해 MeasuredNode[]를 만든다.
 *  - 표(kind==='table'): 전개 후 절대 행(data-composer-row) 높이 + rowspan 안전 경계(computeSafeCutRows).
 *  - repeat(kind==='repeat'): 항목 마커(data-composer-repeat-item) 높이 → 분할 단위(다단이면 행) 높이.
 *  - 그 외: 노드 outer offsetHeight(채움 노드는 auto 토글로 자연높이 측정 후 복원).
 * 측정 중 stale plan 클립(렌더된 행/항목 수 < 진짜 총수)을 감지하면 { clipped: true }를 즉시 반환한다.
 *
 * 호출측 예:
 *   const result = measureBody(el, body, opts);
 *   if (!Array.isArray(result)) { setPagePlan([flattenedBodyIds(body, opts).map(id => ({ nodeId: id }))]); return; }
 *   const plan = planEditPages(result, pageContentH);
 *
 * ⚠️ 그 마지막 줄에 **3번째 인자(EPS)를 넘기지 않는다** — `planEditPages`의 기본값(1)이 **단독
 * 소유**한다(`usePagedPlan.ts` 헤더 「EPS는 `planEditPages`의 기본값(1)이 단독 소유한다」 ·
 * 그 호출부도 일부러 안 넘긴다). 종전 이 예시는 `EPS`를 넘기고 있어서, 베끼면 호스트에 그 상수를
 * 되살려 **합쳐 세 자리였던 종전 상태**로 되돌린다. 지금 이 패키지에 그 이름의 식별자는 **없다**
 * (`pagePlan.ts`의 `EPS`는 그 함수의 기본 파라미터이고, `pagination.ts`의 `EPS = 0.5`는 부동소수
 * 비교 여유로 **다른 것**이다 — 합치지 말 것). (2026-10-13 정정)
 */
export function measureBody(
  container: HTMLElement,
  body: ComposerNode[],
  opts: MeasureBodyOptions = {},
): MeasureResult {
  const el = container;
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  const heightOf = opts.heightOf ?? ((x: HTMLElement) => x.offsetHeight);

  // 표 측정: 전개 후 절대 행(data-composer-row) 높이 + rowspan 안전 경계.
  const measureTable = (id: string) => {
    const heights: number[] = [];
    let rendered = 0;
    for (const tr of el.querySelectorAll<HTMLElement>(`[data-composer-row^="${esc(id)}::"]`)) {
      const parts = (tr.dataset.composerRow ?? '').split('::');
      const r = Number(parts[parts.length - 1]);
      if (Number.isFinite(r)) {
        heights[r] = heightOf(tr);
        rendered += 1;
      }
    }
    // 진짜 총 행수(rowRanges 클립과 무관) — 표 element의 data-composer-table-rows.
    const tableEl = el.querySelector(`[data-composer-node-id="${esc(id)}"][data-composer-table-rows]`);
    const attrTotal = tableEl ? Number(tableEl.getAttribute('data-composer-table-rows')) : NaN;
    const totalRows = Number.isFinite(attrTotal) ? attrTotal : heights.length;
    if (totalRows === 0) return null;
    // 클립 감지: 렌더된 행 수 < 진짜 총 행수 → 현재 plan이 stale(데이터 증가로 새 행이 클립됨).
    const clipped = rendered < totalRows;
    const rowTops: number[] = [];
    let acc = 0;
    for (let r = 0; r < totalRows; r += 1) {
      rowTops[r] = acc;
      acc += heights[r] ?? 0;
    }
    const tableHeight = acc;
    const spanCells: { row: number; rowspan: number }[] = [];
    for (const cellEl of el.querySelectorAll<HTMLTableCellElement>(
      `[data-composer-cell^="${esc(id)}::"]`,
    )) {
      // data-composer-rowspan-full은 keep-together(반복영역 groupStartOnNewPage) 대상 묶음 그룹 셀에만
      // 방출된다 → 여기 spanCells = "쪼개면 안 되는 그룹"뿐. computeSafeCutRows가 그 그룹 관통 경계만
      // unsafe로 내리고 나머지는 전부 자유 컷. 원본(클램프 이전) span을 써야 페이지에 걸쳐 쪼개진 그룹의
      // 관통 경계를 "안전"으로 오판하지 않는다(clip↔measure 피드백 루프 방지). 비대상/continuation/단일 skip.
      const fullAttr = cellEl.getAttribute('data-composer-rowspan-full');
      if (fullAttr == null) continue;
      const rs = Number(fullAttr);
      if (!(rs > 1)) continue;
      const trRow = cellEl.closest<HTMLElement>('[data-composer-row]');
      const parts = (trRow?.dataset.composerRow ?? '').split('::');
      const r = Number(parts[parts.length - 1]);
      if (Number.isFinite(r)) spanCells.push({ row: r, rowspan: rs });
    }
    const safe = computeSafeCutRows(spanCells, totalRows);
    const rowTop = (r: number) => (r < totalRows ? rowTops[r] : tableHeight);
    return { totalRows, tableHeight, rowTop, safe, clipped };
  };

  // ── repeat 측정(중첩 재귀) ──────────────────────────────────────────────
  // 항목 마커(data-composer-repeat-item="{id}::{원본 인덱스}") 높이 → 분할 단위(다단이면
  // 행=columns개 묶음의 max, 아니면 항목) 높이. 표 measureTable과 대칭 구조.
  //
  // 중첩 반복이면 한 단계 안쪽(안쪽 repeat 항목)을 **분할 경계**로 함께 낸다(P6-1).
  // ⚠️ 경계가 있는 단위의 높이는 wrapper offsetHeight를 **쓰지 않는다** — 쪼개진 항목의 wrapper는
  // 낱장마다 있고 그 높이는 계획이 만든 조각 값이라(P6-0 스파이크 C) 되먹으면 진동이 된다.
  // 대신 조각 수에 불변인 `lead + 안쪽 단위 합`으로 재구성한다.

  // 인스턴스 수집은 노드당 1회만 하고 경로 키로 나눠 쓴다(중첩이면 바깥 항목 수만큼 인스턴스).
  const instancesCache = new Map<string, RepeatInstanceHeights[]>();
  const instancesOf = (nodeId: string): RepeatInstanceHeights[] => {
    let got = instancesCache.get(nodeId);
    if (!got) {
      got = collectRepeatInstances(el, nodeId, heightOf);
      instancesCache.set(nodeId, got);
    }
    return got;
  };

  /**
   * repeat 노드 한 **인스턴스**를 측정한다(재귀 — 깊이 무관, 2단 특수 분기 없음).
   * parentPath = 이 인스턴스를 감싼 바깥 항목 경로(최상위면 null). 최상위·비중첩이면
   * rangeKey === nodeId라 결과가 종전 측정과 같은 값이다(회귀 0).
   */
  const measureRepeatInstance = (
    node: RepeatNode,
    parentPath: string | null,
  ): { clipped: boolean; repeat: MeasuredRepeat } | null => {
    const rangeKey = parentPath ? `${parentPath}/${node.id}` : node.id;
    const inst = instancesOf(node.id).find((e) => e.rangeKey === rangeKey);
    const heights = inst?.heights ?? [];
    // 진짜 총 항목수(itemRanges 클립과 무관) — **인스턴스별** 선언값(data-composer-repeat-items).
    // querySelector 단일 조회로 읽으면 중첩에서 첫 인스턴스의 총수가 모든 인스턴스에 적용돼
    // 클립 오탐이 난다(사진1=3건·사진2=2건인데 둘 다 3 — P6-0 스파이크 A).
    const totalItems = inst?.declaredTotal ?? heights.length;
    if (totalItems === 0) return null; // 항목 없음(실데이터 0건 미렌더 등) → 일반 노드로 측정
    // 클립 감지: 렌더된 항목 수 < 진짜 총 항목수 → 현재 plan이 stale(데이터 증가로 새 항목이 클립됨).
    let clipped = (inst?.rendered ?? 0) < totalItems;
    const columns = Math.max(1, node.columns ?? 1);
    const unitCount = Math.ceil(totalItems / columns);
    // 안쪽 분할 경계는 **세로 나열(columns=1)에서만** — 다단은 한 단위가 항목 여러 개라 어느
    // 항목의 안쪽을 자를지가 모호하다(그 축을 측정이 만들지 않으니 계획도 통짜/넘침 그대로).
    // 경계는 안쪽 repeat(단위=항목) 또는 안쪽 표(단위=행)다 — 판정은 렌더와 같은 함수를 쓴다.
    // 유일 경계 승격(2026-09-05): 청크열에 pageBreak:'before' 선언이 있으면 유일 경계 형상이어도
    // **청크 경로**로 — lead/continuation 모델에는 break 어휘가 없어 승격 없인 선언이 영영
    // 무소비다. 판정은 unitHasBreakDecl(순수 — 렌더 RepeatItem의 innerId 게이트와 같은 함수).
    // 선언 없으면 종전 유일 경계 판정 그대로(회귀 0).
    const breakDecl = columns === 1 && unitHasBreakDecl(node.template);
    const innerNode = columns === 1 && !breakDecl ? findSoleNestedBoundary(node.template) : null;
    // 단위 안 채움 표(결정 2026-09-03) — 분할 경계와 **별개 축**(실사용: 경계는 교육기록 repeat,
    // 채움 표는 lead 소속 참석자 표). 세로 나열(columns=1)에서만 — 단위 경로가 항목과 일대일이어야
    // 계획이 `{repeatId}::{u}` 경로로 부여할 수 있다(unitInner와 같은 규약).
    const fillNode = columns === 1 ? findSoleUnitFillTable(node.template) : null;
    const unitInner: (MeasuredRepeatInner | undefined)[] = [];
    // 순차 다경계(결정 2026-09-03 심야): **유일 경계가 없을 때만** 청크열 측정 — 유일 경계 형상은
    // 종전 lead/continuation 경로 그대로(상호 배타 = 회귀 0). 발동 판정은 chunkFlowActive 공유
    // 함수(분할 가능 청크 존재 **또는** pageBreak:'before' 선언 청크 존재 — 2026-09-05 확장:
    // 선언만 있는 단위도 청크열이 서야 계획이 선언을 소비한다). 둘 다 없으면(전부 group에 묻힘 등)
    // 세우지 않는다(종전 통짜/넘침 degrade).
    const chunkNodes = columns === 1 && innerNode === null ? unitFlowChunks(node.template) : null;
    const chunkMode = chunkNodes !== null && chunkFlowActive(chunkNodes);
    const unitChunks: (MeasuredUnitChunks | undefined)[] = [];
    // 분할 단위 = 다단이면 행(같은 행의 columns개 항목이 함께 이동 — grid 행 높이 = 항목 max),
    // 세로 나열이면 항목 1개. 미측정 항목(클립 조각)은 0 — clipped 리셋이 먼저라 실사용 안 됨.
    const unitHeights: number[] = [];
    for (let u = 0; u < unitCount; u += 1) {
      let uh = 0;
      for (let c = 0; c < columns; c += 1) {
        const k = u * columns + c;
        if (k >= totalItems) continue;
        let itemH = heights[k] ?? 0;
        if (innerNode) {
          const itemPath = parentPath ? `${parentPath}/${node.id}::${k}` : `${node.id}::${k}`;
          // 축별로 안쪽 측정치와 경계 컨테이너 속성만 다르고, 그 뒤 판정·재구성은 완전히 같다.
          let axis: MeasuredInnerAxis | null = null;
          let subClipped = false;
          let innerAttr: InnerContainerAttr = 'data-composer-repeat-items';
          if (innerNode.kind === 'repeat') {
            const sub = measureRepeatInstance(innerNode, itemPath);
            if (sub) {
              axis = { repeat: sub.repeat };
              subClipped = sub.clipped;
            }
          } else {
            innerAttr = 'data-composer-table-rows';
            const t = measureTableInstance(el, itemPath, innerNode.id, heightOf);
            if (t) {
              axis = {
                table: {
                  totalRows: t.totalRows,
                  tableHeight: t.tableHeight,
                  rowTop: t.rowTop,
                  safe: t.safe,
                  // 계획기는 안쪽 경계의 startOnNewPage를 읽지 않는다(항목 **안**에서 "새 장"은
                  // 의미가 모호하다 — 안쪽 repeat과 같은 규약). 사실만 그대로 싣는다.
                  startOnNewPage: !!innerNode.startOnNewPage,
                },
              };
              subClipped = t.clipped;
            }
          }
          // 전제 대조 기준(컨테이너 높이와 견줄 단위 합)은 **클립되지 않았을 때만** 넘긴다 —
          // 클립 조각의 컨테이너엔 단위가 일부만 들어 있어, 그대로 견주면 정상 경계가 "부풀었다"로
          // 오판돼 조용히 죽는다(measureNestedBoundary의 NestedBoundaryExpectation 주석).
          const b = axis
            ? measureNestedBoundary(
                el,
                itemPath,
                innerNode.id,
                heightOf,
                innerAttr,
                subClipped
                  ? undefined
                  : {
                      innerTotalH: innerAxisTotalH(axis),
                      unitCount: innerAxisUnitCount(axis),
                    },
              )
            : null;
          // 형상 규약 이탈(lead 조각 부재/중복, 컨테이너 부풀음 등)이면 경계를 주지 않고 **종전
          // 동작으로 degrade**한다. 단 cont-fragments-differ는 예외 — 그건 경계를 버리면 통짜↔분할
          // 진동이라 max(C)로 간다(measureNestedBoundary 주석). 그래서 "그 위반만 허용"으로 판정한다.
          const usable =
            b != null && (b.violation === undefined || b.violation === 'cont-fragments-differ');
          if (axis && b && usable) {
            if (subClipped) clipped = true;
            // 조각 무관 재구성 — 쪼개졌으면 wrapper offsetHeight는 조각 값이라 쓰지 않는다.
            // 표 축의 innerAxisTotalH = 행 높이 합(= tableHeight, 최상위 표 측정과 같은 기준)이고,
            // lead는 같은 조각에서 (항목 − 표 element)로 뺀 값이다. 이 둘이 맞물리려면
            // **경계 컨테이너 높이 === 그 안 단위 높이 합**이어야 한다 — 표는 `border-collapse:
            // collapse`(RenderTemplate tableStyle)라 border-spacing이 없어 대개 성립하지만, 교차축
            // 기본 채움·`height:'fill'`·padding 등으로 컨테이너가 늘어나면 깨진다. 그래서 그 등식을
            // **가정하지 않고 통짜 상태에서 실측 대조**해(inner-container-oversized) 깨졌으면 위
            // usable에서 걸러 종전 통짜로 degrade한다. 여기 도달한 재구성값은 wrapper 실측과
            // 어긋나지 않는다(어긋나면 들어가던 항목이 경계에서 뒤집힌다).
            itemH = b.leadHeight + innerAxisTotalH(axis);
            // 축은 유니온이라 spread 대신 분기해서 싣는다(유니온 spread는 두 축을 다 optional로
            // 뭉개 유니온 arm에 안 맞는다).
            const common = {
              nodeId: innerNode.id,
              path: itemPath,
              leadHeight: b.leadHeight,
              contHeight: b.contHeight,
              // 경계 자신이 채움 표(경계 겸임 — 채움 표가 단위의 유일한 표인 형상). 계획의 부여
              // 지점 ⓒ(전 행 한 조각일 때만 채움) 신호. lead 소속 채움 표는 fillTable이 담당.
              ...(fillNode !== null && fillNode.id === innerNode.id ? { fill: true as const } : {}),
            };
            unitInner[u] = axis.repeat
              ? { ...common, repeat: axis.repeat }
              : { ...common, table: axis.table };
          }
        }
        if (chunkMode && chunkNodes) {
          // 청크별 인스턴스 측정 — 구간은 요소 자연높이, 경계는 기존 축 측정 재사용(안쪽 repeat의
          // unitInner/unitChunks/fillTable이 재귀로 실려 사진 카드 내부 분할 기제가 그대로 탄다).
          const itemPath = parentPath ? `${parentPath}/${node.id}::${k}` : `${node.id}::${k}`;
          const chunks: MeasuredNode[] = [];
          let sum = 0;
          for (const cn of chunkNodes) {
            const breakBefore = cn.pageBreak === 'before' || undefined;
            if (cn.kind === 'repeat') {
              const sub = measureRepeatInstance(cn, itemPath);
              if (sub) {
                if (sub.clipped) clipped = true;
                chunks.push({ id: cn.id, breakBefore, repeat: sub.repeat });
                sum += innerAxisTotalH({ repeat: sub.repeat });
              } else {
                chunks.push({ id: cn.id, breakBefore, nodeHeight: 0 });
              }
            } else if (cn.kind === 'table') {
              const t = measureTableInstance(el, itemPath, cn.id, heightOf);
              if (t) {
                if (t.clipped) clipped = true;
                chunks.push({
                  id: cn.id,
                  breakBefore,
                  pageFill: !!cn.layout?.pageFill,
                  pageFillMode: cn.layout?.pageFillMode,
                  table: {
                    totalRows: t.totalRows,
                    tableHeight: t.tableHeight,
                    rowTop: t.rowTop,
                    safe: t.safe,
                    startOnNewPage: !!cn.startOnNewPage,
                  },
                });
                sum += t.tableHeight;
              } else {
                chunks.push({ id: cn.id, breakBefore, nodeHeight: 0 });
              }
            } else {
              const h = measureOwnedNodeHeight(el, itemPath, cn.id, heightOf);
              chunks.push({ id: cn.id, breakBefore, nodeHeight: h });
              sum += h;
            }
          }
          const chrome = measureUnitChunkChrome(
            el,
            itemPath,
            chunkNodes.map((cn) => cn.id),
            heightOf,
          );
          unitChunks[u] = { chrome, chunks };
          // 조각 무관 재구성 — 분할 상태의 wrapper 실측은 계획이 만든 조각 값이라 되먹지 않는다
          // (unitInner의 lead+단위합 재구성과 같은 불변식).
          itemH = chrome + sum;
        }
        uh = Math.max(uh, itemH);
      }
      unitHeights[u] = uh;
    }
    const repeat: MeasuredRepeat = {
      totalItems,
      columns,
      unitHeights,
      gap: node.gap ?? 0,
      startOnNewPage: !!node.startOnNewPage,
    };
    // 경계를 하나도 못 만들었으면 필드를 아예 두지 않는다(계획 입력이 종전과 동일 — 회귀 0).
    if (unitInner.some((v) => v !== undefined)) repeat.unitInner = unitInner;
    // 순차 다경계 청크열 — 없으면 필드를 아예 두지 않는다(종전 계획 입력 동일 — 회귀 0).
    if (unitChunks.some((v) => v !== undefined)) repeat.unitChunks = unitChunks;
    // breakBefore 라우팅 선언(2026-09-05) — **template 파생**(청크열에 pageBreak:'before' 선언
    // 존재)이라 전 단위·전 측정 회차에 동일하다. 계획 라우팅은 이 선언만 본다 — 측정된 청크
    // 높이(클립·비활성 슬롯·수렴 중간 상태에 따라 유닛별로 달라짐)로 게이트하면 일부 유닛만
    // 슬라이스로 빠진 계획이 고착된다(2026-09-05 dev 실측, pagePlan MeasuredRepeat.breakChunkDecl
    // 주석). 실제 newPage 발동은 계획 소비점의 h>0 게이트가 거른다(비활성 슬롯 무해).
    if (chunkMode && breakDecl) {
      repeat.breakChunkDecl = true;
    }
    // 채움 표 신호 — 없으면 필드를 아예 두지 않는다(종전 템플릿 계획 입력 동일 — 회귀 0).
    if (fillNode !== null) repeat.fillTable = fillNode.id;
    // 조각 잔재 방어(1차 실패 유형의 안전망): 이 인스턴스에 조각난 항목이 있는데(duplicated)
    // 경계를 하나도 못 세웠다면 unitHeights에 **조각 높이**가 섞였다는 뜻이다 — 그대로 계획하면
    // 측정↔계획 피드백 루프가 된다. 통짜 리셋 신호를 내 자가 치유한다(통짜 렌더에는 조각이 없어
    // 다음 측정에서 duplicated가 사라지므로 리셋이 반복되지 않는다). 청크열이 있으면 재구성이
    // 조각 불변이라 이 방어가 불필요하다(unitInner와 동형).
    if (inst?.duplicated === true && repeat.unitInner === undefined && repeat.unitChunks === undefined) {
      clipped = true;
    }
    return { clipped, repeat };
  };

  // DOM에서 노드/표 측정 → MeasuredNode[] 구성(호출측이 planEditPages로 배치 계산).
  // 최상위 branch는 여기서 이미 평탄화돼 있다 — 하강 대상이면 자기 대신 활성 슬롯 자식들이 온다
  // (branch가 없거나 하강 대상이 아니면 body 그대로라 회귀 0).
  const measured: MeasuredNode[] = [];
  // 하강한 branch에서 물려받은 pageBreak — **높이를 차지하는 첫 자식**이 소진한다(계획기가 높이 0
  // 노드의 breakBefore를 무시하므로, 첫 자식이 0이면 여기서 이월하지 않는 한 지시가 유실된다).
  // ⚠️ 이월은 **그 branch의 자식 구간(flat.span) 안에서만** 유효하다. 구간을 안 보면, 자식이 전부
  // 높이 0일 때(예: 반복이 0항목이라 미렌더) 플래그가 살아남아 **branch와 무관한 다음 노드**가
  // 페이지 분리를 받는다(데이터 0건일 때만 나타나는 산발적 분리).
  let inheritedBreak = false;
  let breakSpan: string | undefined;
  for (const flat of flattenBodyForMeasure(body, opts)) {
    const n = flat.node;
    if (flat.inheritedBreak === true) {
      inheritedBreak = true;
      breakSpan = flat.span;
    } else if (inheritedBreak && flat.span !== breakSpan) {
      inheritedBreak = false;
      breakSpan = undefined;
    }
    const tm = n.kind === 'table' ? measureTable(n.id) : null;
    const rmeas = n.kind === 'repeat' ? measureRepeatInstance(n, null) : null;

    // 데이터 증가 등으로 stale plan이 새 행/항목을 클립함 → 호출측이 전체를 통짜로 리셋해 다음 커밋에서
    // 전 행/항목이 렌더되게 하고, 그때 재측정으로 정상 재분할한다(무한루프 없음: 통짜 렌더는 클립이 없어
    // 다음 measure에서 clipped=false). 이 클립 리셋은 setState 의존이라 순수 함수 밖(호출측)에 둔다.
    if (tm?.clipped || rmeas?.clipped) {
      return { clipped: true };
    }

    // pageBreak 'before'(모든 노드 공통) — 계획이 이 노드 앞에서 무조건 페이지를 나눈다.
    // 자기 것 + 하강한 branch에서 물려받아 아직 소진되지 않은 것.
    const breakBefore = n.pageBreak === 'before' || inheritedBreak || undefined;
    // 표/repeat은 항상 높이를 차지한다(0행/0항목이면 tm·rmeas가 null이라 여기 안 온다).
    if (tm || rmeas) {
      inheritedBreak = false;
      breakSpan = undefined;
    }

    if (tm) {
      measured.push({
        id: n.id,
        breakBefore,
        // 채움 표(pageFill 표): 계획이 잔여 slack을 fillSlack으로 산출 → 렌더가 빈 괘선 행 페인팅.
        // 측정 불변식: measureTable은 data-composer-row(실데이터 행) 높이 합산이라 채움 행이
        // 페인팅돼 있어도 tableHeight는 자연 높이 그대로다(group/stack의 height 토글 불필요).
        pageFill: !!n.layout?.pageFill,
        pageFillMode: n.layout?.pageFillMode,
        // 세로 채움 분배(같은 fillSplitGroup 인접 표들이 페이지 예산을 grow로 분배). grow 재사용.
        fillSplitGroup: n.layout?.fillSplitGroup,
        fillGrow: n.layout?.grow,
        table: {
          totalRows: tm.totalRows,
          tableHeight: tm.tableHeight,
          rowTop: tm.rowTop,
          safe: tm.safe,
          // startOnNewPage는 표 노드 필드(discriminated union 좁힘).
          startOnNewPage: n.kind === 'table' ? !!n.startOnNewPage : false,
        },
      });
    } else if (rmeas) {
      // repeat: 항목 단위 분할 대상. 높이는 unitHeights+gap 합산(nodeHeight 불필요 — 클립 조각의
      // 컨테이너 offsetHeight는 총높이가 아니므로 쓰지 않는다). pageFill이면 계획이 채움 우선 처리.
      measured.push({
        id: n.id,
        breakBefore,
        pageFill: !!n.layout?.pageFill,
        pageFillMode: n.layout?.pageFillMode,
        repeat: rmeas.repeat,
      });
    } else {
      const pageFill = !!n.layout?.pageFill;
      // 채움 확장 노드 = pageFill 단일흡수 **또는** fillSplitGroup 세로분배 멤버. 둘 다 렌더가 outer를
      // fillHeight로 확장하므로 측정은 자연높이 probe로 읽어야 확장분을 되먹지 않는다(진동 불변식).
      const needsNaturalProbe = pageFill || n.layout?.fillSplitGroup != null;
      const outerEl = el.querySelector<HTMLElement>(`[data-composer-node-id="${esc(n.id)}"]`);
      let h = 0;
      if (needsNaturalProbe) {
        // 채움 노드: outer 높이를 잠깐 auto로 되돌려(fillHeight 무력화) 자연높이를 읽고 즉시 복원.
        // useLayoutEffect 내부라 paint 전에 끝나 화면 깜빡임 없음. inner height:100%는 auto outer에서
        // 퍼센트 미해석→콘텐츠 높이로 collapse되므로(자식 fill도 함께 collapse) 진동 불변식이 유지된다.
        const naturalEl = el.querySelector<HTMLElement>(`[data-composer-fill-natural="${esc(n.id)}"]`);
        if (outerEl && naturalEl) {
          const saved = outerEl.style.height;
          outerEl.style.height = 'auto';
          h = heightOf(naturalEl); // 강제 reflow 1회 — fill 노드 수만큼(소수)
          outerEl.style.height = saved;
        } else {
          h = outerEl ? heightOf(outerEl) : 0;
        }
      } else {
        h = outerEl ? heightOf(outerEl) : 0;
      }
      // 높이가 있어야 물려받은 지시를 소진한다 — 0이면 다음 노드로 이월(위 inheritedBreak 주석).
      if (h > 0) {
        inheritedBreak = false;
        breakSpan = undefined;
      }
      measured.push({
        id: n.id,
        breakBefore,
        nodeHeight: h,
        pageFill,
        pageFillMode: n.layout?.pageFillMode,
        fillSplitGroup: n.layout?.fillSplitGroup,
        fillGrow: n.layout?.grow,
      });
    }
  }

  return measured;
}
